import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ChangeFinalidade, Finalidade } from 'common/types'
import { HABILITADO, Routes } from 'common/constants'
import { logBridgeError, sendSelectContent } from 'common/helpers'
import { useRequestConfirmationBottomSheetContextBR } from 'contexts/bottomsheet-context/RequestConfirmationBottomSheetContextBR'
import { useAppContext } from 'contexts/app-context/hooks'
import { TCommunications, TUserType } from 'contexts/app-context/types'
import { useParams } from 'react-router-dom'

import { useMeansOfCommunicationBr } from '../../means-of-communication-br/hooks/useMeansOfCommunicationBr'
import { TCommunicatioMethods } from '../../means-of-communication-br/types/MeansOfCommunicationBr.types'
import { MeansOfCommunicationReadyAdapter } from '../MeansOfCommunication.types'

interface BrazilianMeansOfCommunicationAdapterParams {
  communications: TCommunications<TUserType>
  purpose?: Finalidade
}

const areActiveMethodsEqual = (
  currentMethods: Record<string, boolean>,
  nextMethods: Record<string, boolean>,
) => {
  const currentMethodIds = Object.keys(currentMethods)
  const nextMethodIds = Object.keys(nextMethods)

  return (
    currentMethodIds.length === nextMethodIds.length &&
    currentMethodIds.every((methodId) => currentMethods[methodId] === nextMethods[methodId])
  )
}

const normalizeActiveMethods = (metadata: Finalidade['metadados']) => {
  const activeMethods = metadata.reduce<Record<string, boolean>>((methods, method) => {
    if (method.chave !== HABILITADO) methods[method.chave] = method.valor

    return methods
  }, {})

  activeMethods[HABILITADO] = !Object.values(activeMethods).some(Boolean)

  return activeMethods
}

export const useBrazilianMeansOfCommunicationAdapter = ({
  communications,
  purpose,
}: BrazilianMeansOfCommunicationAdapterParams): MeansOfCommunicationReadyAdapter => {
  const { productId } = useParams()
  const { userSelectedEntity } = useAppContext()
  const { mutation, setChangingFinalidade, setShowDialog, setShowSuccessStep } =
    useRequestConfirmationBottomSheetContextBR()
  const { dontDisturbBottomsheetHandler, methods, showBottomsheet } = useMeansOfCommunicationBr({
    meiosDeComunicacao: communications,
    values: purpose?.metadados ?? [],
  })
  const [activeMethods, setActiveMethods] = useState<Record<string, boolean>>({})
  const remoteActiveMethodsRef = useRef<Record<string, boolean> | undefined>(undefined)

  const remoteActiveMethods = useMemo(
    () => (purpose ? normalizeActiveMethods(purpose.metadados) : undefined),
    [purpose],
  )

  useEffect(() => {
    if (!remoteActiveMethods) return

    const hasRemotePreferencesChanged = !areActiveMethodsEqual(
      remoteActiveMethodsRef.current ?? {},
      remoteActiveMethods,
    )

    if (!hasRemotePreferencesChanged) return

    remoteActiveMethodsRef.current = remoteActiveMethods
    setActiveMethods((currentMethods) =>
      areActiveMethodsEqual(currentMethods, remoteActiveMethods)
        ? currentMethods
        : remoteActiveMethods,
    )
  }, [remoteActiveMethods])

  const handleSwitch = useCallback(
    async (method: TCommunicatioMethods, isActive: boolean) => {
      if (!purpose) return

      if (method.isDontDisturb) {
        dontDisturbBottomsheetHandler()
        return
      }

      setActiveMethods((currentMethods) => ({
        ...currentMethods,
        [method.keyBR]: !isActive,
      }))

      try {
        await sendSelectContent({
          flow: 'mean_of_communication_br',
          step: 'notifications_management',
          screen_name: Routes.NOTIFICATIONS_MANAGEMENT,
          element_name: 'toggleCommunicationNotification',
          element_type: 'button',
          parent_name: 'MeanOfCommunication',
          scenario: JSON.stringify({
            screen: Routes.NOTIFICATIONS_MANAGEMENT,
            product: userSelectedEntity,
            activate: `${isActive}`,
            productId: `${productId}`,
            ref_type: 'button',
            content_action: 'touch',
          }),
        })
      } catch (error: unknown) {
        logBridgeError('send mean of communication br tic', error)
      }

      const isLastActiveToggle =
        purpose.metadados.filter(
          (meanOfCommunication) =>
            meanOfCommunication.chave !== HABILITADO && meanOfCommunication.valor !== false,
        ).length === 1
      const isActiveValue = method.keyBR === HABILITADO ? isActive : !isActive

      if ((method.keyBR === HABILITADO && !isActive) || (isActive && isLastActiveToggle)) {
        setShowSuccessStep(false)
        setChangingFinalidade({
          metadados: { chave: HABILITADO, valor: isActiveValue },
          productID: productId,
        } as ChangeFinalidade)
        setShowDialog(true)
        return
      }

      mutation.mutate({
        metadados: {
          chave: method.keyBR,
          valor: method.keyBR === HABILITADO ? isActive : !isActive,
          isNaoPerturbe: method.isDontDisturb,
        },
        productID: productId,
      } as ChangeFinalidade)
    },
    [
      dontDisturbBottomsheetHandler,
      mutation,
      productId,
      purpose,
      setChangingFinalidade,
      setShowDialog,
      setShowSuccessStep,
      userSelectedEntity,
    ],
  )

  return {
    status: 'ready',
    methods: methods.map((method) => {
      const isActive = activeMethods[method.keyBR] ?? method.isActive

      return {
        id: method.keyBR,
        icon: method.icon,
        name: method.name,
        isActive,
        isDisabled: method.isDisabled,
        isDontDisturb: method.isDontDisturb,
        onSwitch: () => handleSwitch(method, isActive),
      }
    }),
    isDontDisturbBottomsheetVisible: showBottomsheet,
    onCloseDontDisturbBottomsheet: dontDisturbBottomsheetHandler,
  }
}
